import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Typography,
  Grid2 as Grid,
  styled,
  CircularProgress,
} from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { CLIENT_UPDATE_CATEGORY_MAPPING } from '../../constants/PricingClientUpdate';
import { setSelectedComponentCategoryKey, setSelectedSizeBasedCategoryKey } from '../../ducks/pricingSlice';
import { useClientUpdatePricingRepo } from '../../hooks/useClientUpdatePricingRepo';
import { filterClientUpdateCategories } from '../../utils/pricingClientUpdateUtils';
import { I18nKeys } from '../../constants/I18nKeys';
import { ComponentCategoryKey, SizeBasedCategoryKey } from '../../constants/ClientUpdateCategoryKey';
import ComingSoonImage from '../../images/coming-soon.png';
import { SupplierDataCategory } from '../../constants/VendorData';

const CategoryCard = styled(Card)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  borderRadius: '8px',
  cursor: 'pointer',
  position: 'relative',
});

const CategoryImage = styled(CardMedia)({
  height: '100%',
  width: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  objectFit: 'contain',
});

const CategoryHeader = styled(CardHeader)({
  position: 'absolute',
  padding: '16px',
});

const CountOverlay = styled(Typography)({
  position: 'absolute',
  padding: '16px',
  margin: 0,
  bottom: 0,
  right: 0,
});

export const PricingCategories: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const searchTerm = useAppSelector((state) => state.search.searchTerm);
  const secondaryMenuItem = useAppSelector((state) => state.menu.secondaryMenuItem);
  const isInitializingBranch = useAppSelector((state) => state.clientData.isInitializingBranch);

  const { clientUpdateCategoriesList = [], isLoadingClientUpdateCategoriesList } = useClientUpdatePricingRepo({
    useClientUpdateCategories: true,
  });

  const filteredComponentCategories = useMemo(
    () => filterClientUpdateCategories(searchTerm, clientUpdateCategoriesList, t),
    [searchTerm, clientUpdateCategoriesList, t],
  );

  if (isLoadingClientUpdateCategoriesList || isInitializingBranch) {
    return (
      <Box display="flex" flex={1} alignItems="center" justifyContent="center">
        <CircularProgress color="secondary" sx={{ mt: '-20px' }} />
      </Box>
    );
  }
  return (
    <Box display="flex" flex={1}>
      <Box width="100%" height="fit-content">
        <Grid
          container
          gap="24px"
          sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'stretch' }}
        >
          {filteredComponentCategories.length > 0 ? (
            filteredComponentCategories.map(({ key, count, disabled = false }) => (
              <Grid key={key} sx={{ flex: '0 1 225px', height: '225px', aspectRatio: '1 / 1' }}>
                <CategoryCard
                  elevation={0}
                  style={{
                    ...(disabled ? { opacity: 0.4, cursor: 'default' } : {}),
                  }}
                  onClick={() => {
                    if (disabled) return;
                    dispatch(
                      secondaryMenuItem === SupplierDataCategory.Components
                        ? setSelectedComponentCategoryKey(key as ComponentCategoryKey)
                        : setSelectedSizeBasedCategoryKey(key as SizeBasedCategoryKey),
                    );
                  }}
                >
                  <CategoryHeader
                    color="text.primary"
                    slotProps={{
                      title: { variant: 'body1', fontSize: '16px', fontWeight: 700, color: 'text.primary' },
                    }}
                    title={t(CLIENT_UPDATE_CATEGORY_MAPPING[key]?.label)}
                  />
                  <CardContent sx={{ padding: '24px 12px 0px !important', height: '100%' }}>
                    <CategoryImage>
                      <img
                        src={disabled ? ComingSoonImage : CLIENT_UPDATE_CATEGORY_MAPPING[key]?.image}
                        alt="component"
                        draggable={false}
                        style={{ height: '100%', width: 'auto' }}
                      />
                    </CategoryImage>
                    {!disabled && (
                      <CountOverlay gutterBottom variant="body1" color="text.secondary">
                        {count}
                      </CountOverlay>
                    )}
                  </CardContent>
                </CategoryCard>
              </Grid>
            ))
          ) : (
            <Typography
              variant="body1"
              component="div"
              style={{
                margin: '10% auto',
              }}
            >
              {t(I18nKeys.PricingComponentNoCategoriesFound)}
            </Typography>
          )}
        </Grid>
      </Box>
    </Box>
  );
};
